import { useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Power, PauseCircle, Wallet, Copy as CopyIcon, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import { errorMessage } from "@/api/errors";
import {
  batchAction,
  getBatchJob,
  type BatchActionPath,
  type BatchBody,
  type BatchLevelPath,
  type BatchResponse,
  type BatchJobView,
} from "./api";
import BatchBudgetDialog from "./BatchBudgetDialog";
import BatchCopyDialog from "./BatchCopyDialog";

interface Props {
  accountId: string;
  level: BatchLevelPath;
  selectedIds: string[];
  onClear: () => void;
  onSuccess: () => void;
  /** Ad 层级不支持预算批量。 */
  supportsBudget?: boolean;
  resourceLabel: string; // "Campaign" / "AdSet" / "Ad"
}

/** 当前正在轮询的异步任务（按 jobId 一个） */
interface PollingJob {
  jobId: string;
  label: string; // e.g. "批量暂停 Campaign × 30"
}

export default function BatchActionBar({
  accountId,
  level,
  selectedIds,
  onClear,
  onSuccess,
  supportsBudget = true,
  resourceLabel,
}: Props) {
  const qc = useQueryClient();
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [polling, setPolling] = useState<PollingJob | null>(null);

  const count = selectedIds.length;

  const run = useMutation({
    mutationFn: async (args: { action: BatchActionPath; body: BatchBody; label: string }) => {
      const resp = await batchAction(accountId, level, args.action, args.body);
      return { ...args, resp };
    },
    onSuccess: ({ resp, label }) => {
      handleResponse(resp, label);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  function handleResponse(resp: BatchResponse, label: string) {
    if (resp.mode === "SYNC" && resp.result) {
      const { successCount, failedCount } = resp.result;
      if (failedCount === 0) {
        toast.success(`${label}：${successCount} 条全部成功`);
      } else if (successCount === 0) {
        toast.error(`${label}：${failedCount} 条全部失败`);
      } else {
        toast.warning(`${label}：成功 ${successCount} / 失败 ${failedCount}`);
      }
      onSuccess();
      onClear();
    } else if (resp.mode === "ASYNC" && resp.job) {
      toast.message(`${label} 已入队`, {
        description: `${resp.job.total} 条任务进入后台执行（jobId: ${resp.job.jobId.slice(0, 8)}…）`,
      });
      setPolling({ jobId: resp.job.jobId, label });
      onClear();
    }
  }

  const setStatus = (status: "ACTIVE" | "PAUSED") =>
    run.mutate({
      action: "status",
      body: { ids: selectedIds, status },
      label: `批量${status === "ACTIVE" ? "启用" : "暂停"} ${resourceLabel} × ${count}`,
    });

  const doDelete = () => {
    run.mutate({
      action: "delete",
      body: { ids: selectedIds },
      label: `批量删除 ${resourceLabel} × ${count}`,
    });
    setDeleteOpen(false);
  };

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-muted/40">
        <span className="text-sm font-medium">已选 {count} 项</span>
        <button
          onClick={onClear}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <X className="h-3 w-3" /> 清除
        </button>
        <div className="ml-auto flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" disabled={run.isPending}>
                <Power className="h-4 w-4 mr-1" /> 启停
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setStatus("ACTIVE")}>
                <Power className="h-4 w-4 mr-2" /> 全部启用
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatus("PAUSED")}>
                <PauseCircle className="h-4 w-4 mr-2" /> 全部暂停
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {supportsBudget && (
            <Button size="sm" variant="outline" disabled={run.isPending} onClick={() => setBudgetOpen(true)}>
              <Wallet className="h-4 w-4 mr-1" /> 改预算
            </Button>
          )}

          <Button size="sm" variant="outline" disabled={run.isPending} onClick={() => setCopyOpen(true)}>
            <CopyIcon className="h-4 w-4 mr-1" /> 复制
          </Button>

          <Button size="sm" variant="destructive" disabled={run.isPending} onClick={() => setDeleteOpen(true)}>
            <Trash2 className="h-4 w-4 mr-1" /> 删除
          </Button>
        </div>
      </div>

      {budgetOpen && (
        <BatchBudgetDialog
          open={budgetOpen}
          onOpenChange={setBudgetOpen}
          count={count}
          onSubmit={(daily, lifetime) =>
            run.mutate({
              action: "budget",
              body: { ids: selectedIds, dailyCents: daily, lifetimeCents: lifetime },
              label: `批量改预算 ${resourceLabel} × ${count}`,
            })
          }
        />
      )}

      {copyOpen && (
        <BatchCopyDialog
          open={copyOpen}
          onOpenChange={setCopyOpen}
          count={count}
          resourceLabel={resourceLabel}
          supportsSchedule={level !== "ads"}
          onSubmit={({ newStatus, startTime, endTime }) =>
            run.mutate({
              action: "copy",
              body: { ids: selectedIds, newStatus, startTime, endTime },
              label: `批量复制 ${resourceLabel} × ${count}`,
            })
          }
        />
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除？</AlertDialogTitle>
            <AlertDialogDescription>
              即将删除 {count} 个 {resourceLabel}，此操作会调用 Meta 接口逐条执行，不可撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete}>确认删除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {polling && (
        <BatchJobPoller
          accountId={accountId}
          jobId={polling.jobId}
          label={polling.label}
          onDone={(job) => {
            const { successCount, failedCount } = job;
            if (failedCount === 0) toast.success(`${polling.label}：${successCount} 条全部成功`);
            else if (successCount === 0) toast.error(`${polling.label}：${failedCount} 条全部失败`);
            else toast.warning(`${polling.label}：成功 ${successCount} / 失败 ${failedCount}`);
            qc.invalidateQueries({ queryKey: ["batch-job", accountId, polling.jobId] });
            onSuccess();
            setPolling(null);
          }}
          onClose={() => setPolling(null)}
        />
      )}
    </>
  );
}

interface PollerProps {
  accountId: string;
  jobId: string;
  label: string;
  onDone: (job: BatchJobView) => void;
  onClose: () => void;
}

function BatchJobPoller({ accountId, jobId, label, onDone, onClose }: PollerProps) {
  const q = useQuery({
    queryKey: ["batch-job", accountId, jobId],
    queryFn: () => getBatchJob(accountId, jobId),
    refetchInterval: (query) => {
      const j = query.state.data;
      if (!j) return 1500;
      if (j.status === "RUNNING" || j.status === "QUEUED") return 1500;
      return false; // terminal
    },
  });

  const j = q.data;
  if (j && ["SUCCEEDED", "PARTIAL", "FAILED"].includes(j.status)) {
    // 触发一次终态回调，但避免在渲染期改父 state - 用 effect 替代
    queueMicrotask(() => onDone(j));
  }

  const pct = j
    ? j.total === 0
      ? 0
      : Math.round(((j.successCount + j.failedCount) / j.total) * 100)
    : 0;

  return (
    <div className="fixed bottom-4 right-4 w-80 rounded-md border border-border bg-background shadow-lg p-3 z-50">
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium">{label}</div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
      <Progress value={pct} className="mt-2" />
      <div className="text-xs text-muted-foreground mt-1">
        {j ? `${j.successCount + j.failedCount} / ${j.total} · ${j.status}` : "排队中…"}
      </div>
    </div>
  );
}
